import { create } from 'zustand';
import type { AgentEvent, AutoChargeEvent, ChargerState, CmdLogRow, AutonomyEvent, MediaLibMessage, Robot, NavStuck, RobotStatus } from '../api/types';

export interface RobotLogEntry {
  id: number;
  t: number;
  kind: 'event' | 'cmd' | 'ok' | 'err' | 'heard' | 'info';
  label: string;
  text: string;
}

export type WsState = 'connecting' | 'open' | 'closed';

interface LiveState {
  wsState: WsState;
  robots: Record<string, Robot>;
  robotOrder: string[];
  log: Record<string, RobotLogEntry[]>;
  heard: Record<string, { text: string; lang?: string; engine?: string; t: number }>;
  agent: Record<string, AgentEvent[]>;
  /** §14: runId → sessionId, and a badge for sessions that finished/errored in the background. */
  runSession: Record<string, string>;
  sessionBadge: Record<string, 'done' | 'error'>;
  activeSession: string | null;
  setActiveSession: (id: string | null) => void;
  linkRun: (runId: string, sessionId: string) => void;
  mediaLib: Record<string, MediaLibMessage>;
  /** §19 live transcript per robot (newest last) and last reported state. */
  autonomyLog: Record<string, (AutonomyEvent & { t: number; id: number })[]>;
  autonomyState: Record<string, string>;
  /** §18 last schedule event per schedule id. */
  scheduleEvents: Record<string, { event: string; t: number; data: Record<string, unknown> }>;
  /** §20 latest navStuck event per robot (with receive time). */
  navStuck: Record<string, NavStuck & { t: number }>;
  /** server command log rows pushed over /ws/ui (newest first), merged with the REST page in the panel */
  cmdLog: Record<string, CmdLogRow[]>;
  addCmdLog: (serial: string, row: CmdLogRow) => void;
  /** §23 client-side depth history per robot (last 60 s): receive time + sectors (cm) + robot `at`. */
  depthHist: Record<string, DepthSample[]>;
  /** §23 camera people history per robot (last 60 s). */
  peopleHist: Record<string, PeopleSample[]>;
  /** last sensor event per robot+kind (receive time), for "data age" */
  sensorSeen: Record<string, Record<string, number>>;
  noteSensor: (serial: string, kind: string) => void;
  /** §21.x charger occupancy per map id (from /ws/ui "chargers" or the REST fetch). */
  chargers: Record<string, ChargerState[]>;
  setChargers: (mapId: string, list: ChargerState[]) => void;
  /** §21 latest autoCharge event per robot. */
  autoCharge: Record<string, AutoChargeEvent & { t: number }>;
  setAutoCharge: (serial: string, ev: AutoChargeEvent | null) => void;
  setNavStuck: (serial: string, ev: NavStuck | null) => void;
  addAutonomy: (ev: AutonomyEvent) => void;
  /** history backfill: older events go before the live ones (keeps their own timestamps) */
  prependAutonomy: (serial: string, events: AutonomyEvent[]) => void;
  clearAutonomy: (serial: string) => void;
  setScheduleEvent: (id: string, event: string, data: Record<string, unknown>) => void;
  setWsState: (s: WsState) => void;
  setRobots: (list: Robot[]) => void;
  setStatus: (serial: string, online: boolean, status: RobotStatus | null) => void;
  addLog: (serial: string, kind: RobotLogEntry['kind'], label: string, text?: string) => void;
  clearLog: (serial: string) => void;
  setHeard: (serial: string, h: { text: string; lang?: string; engine?: string }) => void;
  addAgent: (ev: AgentEvent) => void;
  setAgentEvents: (runId: string, events: AgentEvent[]) => void;
  setMediaLib: (m: MediaLibMessage) => void;
}

let logSeq = 1;

export interface DepthSample {
  /** browser receive time (ms) */
  t: number;
  /** robot timestamp (ms), when sent */
  at: number | null;
  sectors: number[];
  maxCm: number;
}
const DEPTH_KEEP_MS = 60_000;

export interface PeopleSample {
  t: number;
  people: { id: string; b: number; d: number; c: number; at: number | null }[];
}

/** Append the camera people list when it changed (robot `at` / ids / distances). */
function pushPeople(hist: Record<string, PeopleSample[]>, serial: string, status: RobotStatus | null): Record<string, PeopleSample[]> {
  const ppl = status?.sensors?.people;
  if (!Array.isArray(ppl)) return hist;
  const list = hist[serial] ?? [];
  const people = ppl
    .filter((p) => p && typeof p.distanceM === 'number' && typeof p.bearingDeg === 'number')
    .map((p) => ({ id: String(p.id), b: p.bearingDeg, d: p.distanceM, c: typeof p.confidence === 'number' ? p.confidence : 1, at: typeof p.at === 'number' ? p.at : null }));
  const key = people.map((p) => `${p.id}:${p.at ?? ''}:${p.d}:${p.b}`).join('|');
  const last = list[list.length - 1];
  if (last && last.people.map((p) => `${p.id}:${p.at ?? ''}:${p.d}:${p.b}`).join('|') === key) return hist;
  const now = Date.now();
  return { ...hist, [serial]: [...list.filter((x) => now - x.t <= DEPTH_KEEP_MS), { t: now, people }] };
}

/** Append a new depth reading (only when the robot's `at` / values changed) and drop samples older than 60 s. */
function pushDepth(hist: Record<string, DepthSample[]>, serial: string, status: RobotStatus | null): Record<string, DepthSample[]> {
  const d = status?.sensors?.depth;
  if (!d || !Array.isArray(d.sectors) || !d.sectors.length) return hist;
  const list = hist[serial] ?? [];
  const last = list[list.length - 1];
  const at = typeof d.at === 'number' ? d.at : null;
  if (last && (at !== null ? last.at === at : last.sectors.join(',') === d.sectors.join(','))) return hist;
  const now = Date.now();
  const next = [...list.filter((x) => now - x.t <= DEPTH_KEEP_MS), { t: now, at, sectors: d.sectors.map(Number), maxCm: Number(d.maxCm) || 120 }];
  return { ...hist, [serial]: next };
}
const MAX_LOG = 200;

export const useLive = create<LiveState>((set) => ({
  wsState: 'closed',
  robots: {},
  robotOrder: [],
  log: {},
  heard: {},
  agent: {},
  runSession: {},
  sessionBadge: {},
  activeSession: null,
  setActiveSession: (id) =>
    set((s) => {
      if (!id || !s.sessionBadge[id]) return { activeSession: id };
      const sessionBadge = { ...s.sessionBadge };
      delete sessionBadge[id];
      return { activeSession: id, sessionBadge };
    }),
  linkRun: (runId, sessionId) => set((s) => ({ runSession: { ...s.runSession, [runId]: sessionId } })),
  mediaLib: {},
  autonomyLog: {},
  autonomyState: {},
  scheduleEvents: {},
  navStuck: {},
  autoCharge: {},
  chargers: {},
  setChargers: (mapId, list) => set((s) => ({ chargers: { ...s.chargers, [mapId]: list } })),
  setAutoCharge: (serial, ev) =>
    set((s) => {
      const autoCharge = { ...s.autoCharge };
      if (ev) autoCharge[serial] = { ...ev, t: Date.now() };
      else delete autoCharge[serial];
      return { autoCharge };
    }),
  setNavStuck: (serial, ev) =>
    set((s) => {
      const navStuck = { ...s.navStuck };
      if (ev) navStuck[serial] = { ...ev, t: Date.now() };
      else delete navStuck[serial];
      return { navStuck };
    }),
  addAutonomy: (ev) =>
    set((s) => {
      const list = [...(s.autonomyLog[ev.serial] ?? []), { ...ev, t: Date.now(), id: logSeq++ }].slice(-MAX_LOG);
      const out: Partial<LiveState> = { autonomyLog: { ...s.autonomyLog, [ev.serial]: list } };
      if (ev.kind === 'state' && typeof ev.state === 'string') out.autonomyState = { ...s.autonomyState, [ev.serial]: ev.state };
      if (ev.kind === 'ended') out.autonomyState = { ...s.autonomyState, [ev.serial]: 'ended' };
      return out;
    }),
  prependAutonomy: (serial, events) =>
    set((s) => {
      const older = events.map((ev) => ({ ...ev, t: typeof ev.ts === 'string' ? Date.parse(ev.ts) || Date.now() : typeof ev.ts === 'number' ? ev.ts : Date.now(), id: logSeq++ }));
      const list = [...older, ...(s.autonomyLog[serial] ?? [])].slice(-MAX_LOG);
      return { autonomyLog: { ...s.autonomyLog, [serial]: list } };
    }),
  clearAutonomy: (serial) =>
    set((s) => {
      const autonomyState = { ...s.autonomyState };
      delete autonomyState[serial];
      return { autonomyLog: { ...s.autonomyLog, [serial]: [] }, autonomyState };
    }),
  setScheduleEvent: (id, event, data) => set((s) => ({ scheduleEvents: { ...s.scheduleEvents, [id]: { event, t: Date.now(), data } } })),
  setWsState: (wsState) => set({ wsState }),
  setRobots: (list) =>
    set((s) => {
      const robots: Record<string, Robot> = {};
      let depthHist = s.depthHist;
      for (const r of list) {
        robots[r.serial] = r;
        if (r.online) depthHist = pushDepth(depthHist, r.serial, r.status ?? null);
      }
      return { robots, robotOrder: list.map((r) => r.serial), depthHist };
    }),
  setStatus: (serial, online, status) =>
    set((s) => {
      const r = s.robots[serial];
      if (!r) return {};
      const depthHist = online ? pushDepth(s.depthHist, serial, status) : s.depthHist;
      const peopleHist = online ? pushPeople(s.peopleHist, serial, status) : s.peopleHist;
      return { robots: { ...s.robots, [serial]: { ...r, online, status: status ?? r.status, lastSeen: online ? new Date().toISOString() : r.lastSeen } }, depthHist, peopleHist };
    }),
  cmdLog: {},
  addCmdLog: (serial, row) =>
    set((s) => {
      const cur = s.cmdLog[serial] ?? [];
      const list = cur.some((r) => r.id === row.id) ? cur.map((r) => (r.id === row.id ? row : r)) : [row, ...cur].slice(0, 300);
      return { cmdLog: { ...s.cmdLog, [serial]: list } };
    }),
  depthHist: {},
  peopleHist: {},
  sensorSeen: {},
  noteSensor: (serial, kind) => set((s) => ({ sensorSeen: { ...s.sensorSeen, [serial]: { ...(s.sensorSeen[serial] ?? {}), [kind]: Date.now() } } })),
  addLog: (serial, kind, label, text = '') =>
    set((s) => {
      const list = [{ id: logSeq++, t: Date.now(), kind, label, text }, ...(s.log[serial] ?? [])].slice(0, MAX_LOG);
      return { log: { ...s.log, [serial]: list } };
    }),
  clearLog: (serial) => set((s) => ({ log: { ...s.log, [serial]: [] } })),
  setHeard: (serial, h) => set((s) => ({ heard: { ...s.heard, [serial]: { ...h, t: Date.now() } } })),
  addAgent: (ev) =>
    set((s) => {
      const list = s.agent[ev.runId] ?? [];
      // de-dup (same event may arrive via REST history and WS)
      if (ev.ts && list.some((e) => e.ts === ev.ts && e.kind === ev.kind && e.toolUseId === ev.toolUseId && e.text === ev.text)) return {};
      const sid = ev.sessionId ?? s.runSession[ev.runId];
      const out: Partial<LiveState> = { agent: { ...s.agent, [ev.runId]: [...list, ev] } };
      if (ev.sessionId && s.runSession[ev.runId] !== ev.sessionId) out.runSession = { ...s.runSession, [ev.runId]: ev.sessionId };
      if (sid && sid !== s.activeSession && (ev.kind === 'done' || ev.kind === 'error')) out.sessionBadge = { ...s.sessionBadge, [sid]: ev.kind };
      return out;
    }),
  setAgentEvents: (runId, events) => set((s) => ({ agent: { ...s.agent, [runId]: events } })),
  setMediaLib: (m) => set((s) => ({ mediaLib: { ...s.mediaLib, [m.id]: m } })),
}));

export const robotName = (serial: string) => useLive.getState().robots[serial]?.name ?? serial;

export function useRobotList(): Robot[] {
  const robots = useLive((s) => s.robots);
  const order = useLive((s) => s.robotOrder);
  return order.map((k) => robots[k]).filter((r): r is Robot => Boolean(r));
}
